import React, { useState, useEffect } from 'react';
import { Image, StyleSheet, Text, TouchableOpacity, View, ScrollView } from 'react-native';
import {
  getDateString,
  isBase64Image,
  FontScale,
  getFluidSpacing
} from '../../../../helpers/utils';
import { Credential } from '../../../../types/credentials';

interface CardDetailsProps {
  credential: Credential;
}

/**
 * CardDetails is a component that displays the details of a credential.
 * It is used in the CredentialCard component.
 *
 * @param {CardDetailsProps} { credential }
 * @return {React.FC}
 */
const CardDetails: React.FC<CardDetailsProps> = ({ credential }) => {
  const [visibility, setVisibility] = useState<Record<string, boolean>>({});
  const [allVisible, setAllVisible] = useState(false);

  const mapKey = (key: string): string => {
    const mapped = key.charAt(0).toUpperCase() + key.slice(1);
    return mapped.replace(/_/g, ' ');
  };

  const toggleVisibility = (name: string) => {
    setVisibility((prev) => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  const toggleAllVisibility = () => {
    const newVisibility = credential.attributes.reduce(
      (acc, item) => ({
        ...acc,
        [item.name]: !allVisible
      }),
      {}
    );
    setVisibility(newVisibility);
    setAllVisible(!allVisible);
  };

  useEffect(() => {
    const allVisibleNow = credential.attributes.every((item) => visibility[item.name]);
    setAllVisible(allVisibleNow);
  }, [visibility, credential.attributes]);

  return (
    <View style={styles.container}>
      <View style={styles.cardAttributesSectionHeader}>
        <View style={styles.cardAttributesSectionTitle}>
          <Text style={styles.attributeName}>Issued By</Text>
          <Text style={styles.attributeValue}>{credential.issuer}</Text>
        </View>
        <TouchableOpacity onPress={toggleAllVisibility}>
          <Text style={styles.toggleButton}>{allVisible ? 'Hide All' : 'Show All'}</Text>
        </TouchableOpacity>
      </View>
      <View style={styles.scrollWrapper}>
        <ScrollView style={styles.scrollContainer} contentContainerStyle={styles.scrollContent}>
          <View style={styles.cardAttributes}>
            {credential.attributes.map((item, index) => {
              let value = item.value;
              if (item.name.toLowerCase().includes('date') && !isNaN(Number(value))) {
                value = getDateString(Number(value));
              }
              return (
                <View key={item.name} style={styles.cardAttribute}>
                  <View style={styles.attributeTitle}>
                    <Text style={styles.attributeName}>{mapKey(item.name)}</Text>
                    <TouchableOpacity onPress={() => toggleVisibility(item.name)}>
                      <Text style={styles.toggleButton}>
                        {visibility[item.name] ? 'Hide' : 'View'}
                      </Text>
                    </TouchableOpacity>
                  </View>
                  <View
                    style={index !== credential.attributes.length - 1 && styles.attributeBorder}>
                    {visibility[item.name] ? (
                      isBase64Image(value) || value.includes('https://') ? (
                        <View style={styles.attributeImageContainer}>
                          <Image source={{ uri: value }} style={styles.attributeImage} />
                        </View>
                      ) : (
                        <Text style={styles.attributeValue}>{value || 'N/A'}</Text>
                      )
                    ) : (
                      <Text style={styles.hiddenAttributeValue}>{'••••••'}</Text>
                    )}
                  </View>
                </View>
              );
            })}
          </View>
        </ScrollView>
      </View>
    </View>
  );
};

export default CardDetails;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: 0,
    maxHeight: '100%'
  },
  attributeName: {
    fontSize: FontScale.fieldLabel(),
    color: '#30394C',
    fontWeight: '400'
  },
  attributeValue: {
    fontSize: FontScale.fieldValue(),
    color: '#30394C',
    fontWeight: '400'
  },
  hiddenAttributeValue: {
    fontSize: FontScale.fieldHidden(),
    color: '#30394C',
    fontWeight: '400'
  },
  cardAttributesSectionHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderColor: '#CECECE',
    paddingBottom: getFluidSpacing(8, 10, 12),
    marginBottom: getFluidSpacing(6, 8, 10)
  },
  cardAttributesSectionTitle: {
    marginBottom: getFluidSpacing(4, 5, 6),
    fontSize: FontScale.cardHeader(),
    fontWeight: '700'
  },
  cardAttributes: {
    display: 'flex',
    borderRadius: 6,
    overflow: 'hidden'
  },
  cardAttribute: {
    paddingTop: getFluidSpacing(8, 10, 12),
    paddingRight: getFluidSpacing(8, 10, 12),
    paddingBottom: getFluidSpacing(8, 10, 12),
    paddingLeft: getFluidSpacing(8, 10, 12)
  },
  attributeImageContainer: {
    marginTop: getFluidSpacing(4, 5, 6),
    width: '100%',
    maxWidth: 200,
    aspectRatio: '1/1',
    overflow: 'hidden',
    borderRadius: 6
  },
  attributeImage: {
    width: '100%',
    height: '100%'
  },
  toggleButton: {
    fontSize: FontScale.button(),
    color: '#007BFF',
    fontWeight: '400',
    textAlign: 'right'
  },
  attributeTitle: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  attributeBorder: {
    borderBottomWidth: 1,
    borderColor: '#CECECE',
    paddingBottom: getFluidSpacing(3, 4, 5)
  },
  scrollWrapper: {
    flex: 1,
    minHeight: 0
  },
  scrollContainer: {
    flex: 1,
    maxHeight: '100%'
  },
  scrollContent: {
    flexGrow: 1,
    paddingBottom: getFluidSpacing(4, 6, 8)
  }
});
